import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Modal } from 'antd';
import cn from 'classnames';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useIwBreakPoints } from '@/_pages/interwill/hooks/useIwBreakPoints';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { Dashboard } from './Dashboard';
import { DiagnosticCards } from './DiagnosticCards';
import { ExitButton } from './ExitButton';
import { InfoPanel } from './InfoPanel';
import { Introduction } from './Introduction';
import { OnboardingStepData, OnboardingSteps } from './types';

const STEPS: Partial<Record<OnboardingSteps, OnboardingStepData>> = {
  [OnboardingSteps.DASHBOARD]: {
    className: 'dashboard',
    position: 'bottom',
    padding: 24,
    element: <Dashboard />,
  },
  [OnboardingSteps.DIAGNOSTIC_CARDS]: {
    className: 'diagnosticCards',
    position: 'top',
    padding: 0,
    element: <DiagnosticCards />,
  },
  [OnboardingSteps.OVERVIEW]: {
    className: 'overview',
    position: 'bottom',
    padding: 24,
    element: <Introduction />,
  },
  [OnboardingSteps.INFO_PANEL]: {
    className: 'infopanel',
    position: 'left',
    padding: 0,
    element: <InfoPanel />,
  },
  [OnboardingSteps.EXIT]: {
    className: 'exit',
    position: 'bottom-right',
    padding: 16,
    element: <ExitButton />,
  },
};
const FADE_DURATION = 350;

type OnboardingProps = {
  step: OnboardingSteps;
  maxSteps: number;
  onNext: () => void;
  onBack: () => void;
};

export const Onboarding: FC<OnboardingProps> = ({ step, maxSteps, onNext, onBack }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'welcome' });

  const modals = useModalsStore();

  const { isMobile } = useIwBreakPoints();

  const [position, setPosition] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [displayStep, setDisplayStep] = useState<OnboardingSteps>(step);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(false);

    const timer = setTimeout(() => {
      setIsReady(true);
      setIsVisible(true);
      setDisplayStep(step);
    }, FADE_DURATION);

    return () => clearTimeout(timer);
  }, [step]);

  const modalPosition = useMemo<CSSProperties>(() => {
    const stepData = STEPS[displayStep];
    if (!stepData || !containerRef.current) return {};
    const containerRect = containerRef.current.getBoundingClientRect();

    if (isMobile) {
      return {
        left: 16,
        right: 16,
        top: position.top + containerRect.height + 16,
        position: 'fixed',
      };
    }

    const modal = document.querySelector('.ant-modal-content');
    const height = modal?.getBoundingClientRect().height ?? 0;
    const width = modal?.getBoundingClientRect().width ?? 0;

    switch (stepData.position) {
      case 'left':
        return {
          top: `calc(50% - ${height}px / 2)`,
          left: position.left - width - 16,
        };

      case 'top':
        return {
          top: position.top - 16 - height,
          left: position.left + position.width / 2 - 358 / 2,
        };

      case 'bottom-right':
        return {
          top: position.top + containerRect.height + 16,
          left: 66,
        };

      case 'bottom':
      default:
        return {
          top: position.top + containerRect.height + 16,
          left: position.left + position.width / 2 - 358 / 2,
        };
    }
  }, [position, containerRef, displayStep, isMobile]);

  const updatePosition = () => {
    const modal = document.querySelector('.ant-modal-content');
    const height = modal?.getBoundingClientRect().height ?? 0;
    const width = modal?.getBoundingClientRect().width ?? 0;

    if (isMobile && displayStep !== OnboardingSteps.EXIT) {
      setPosition({
        left: 16,
        top: 44,
        width: width,
        height: window.innerHeight - height - 32 - 44,
      });
      return;
    }

    const stepData = STEPS[displayStep];
    if (!stepData) return;

    const el = document.querySelector<HTMLDivElement>(`.${stepData.className}`);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    setPosition({
      left: rect.left - stepData.padding,
      top: rect.top - stepData.padding,
      width: rect.width + stepData.padding * 2,
      height: rect.height + stepData.padding * 2,
    });
  };

  useEffect(() => {
    updatePosition();

    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [displayStep, isMobile, position]);

  return (
    <StyledModal
      style={{
        ...modalPosition,
        margin: 0,
        padding: 0,
        opacity: isVisible ? 1 : 0,
      }}
      width={isMobile ? 'calc(100% - 32px)' : 358}
      open={true}
      footer={null}
      closable={isReady}
      maskClosable={false}
      onCancel={() => {
        modals.closeModal();
        modals.openModal({
          type: 'ExitOnboardingModal',
          savedStep: step,
        });
      }}
    >
      <Content
        className={cn({
          top: STEPS[displayStep]?.position === 'top',
          bottom: STEPS[displayStep]?.position === 'bottom',
          left: STEPS[displayStep]?.position === 'left',
          bottomRight: STEPS[displayStep]?.position === 'bottom-right',
        })}
        style={{
          opacity: isReady ? 1 : 0,
          display: isReady ? 'block' : 'none',
        }}
      >
        <Title>{t(`step${displayStep}.title`)}</Title>
        <Text>{t(`step${displayStep}.text`)}</Text>
        <Footer>
          <ButtonIw type="default" onClick={onBack}>
            {t('btnBack')}
          </ButtonIw>
          <ProgressText>
            {displayStep}/{maxSteps}
          </ProgressText>
          <ButtonIw onClick={onNext}>{t('btnNext')}</ButtonIw>
        </Footer>
      </Content>
      <FixedContainer
        ref={containerRef}
        style={{
          left: position.left,
          top: position.top,
          width: position.width,
          maxHeight: isMobile ? position.height : undefined,
          opacity: isVisible ? 1 : 0,
        }}
      >
        {STEPS[displayStep]?.element}
      </FixedContainer>
    </StyledModal>
  );
};

const Title = styled.div`
  font-size: 22px;
  font-weight: 700;
`;

const StyledModal = styled(Modal)`
  &&& {
    transition: opacity ${FADE_DURATION}ms 50ms ease-in-out;
    .ant-modal-content {
      padding: 0;
    }

    .ant-modal-close {
      top: 16px;

      right: 16px;
    }
  }
`;

const Content = styled.div`
  padding: 16px;
  color: ${COLORS.systemDark300};

  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 12px 14px 12px;
    border-color: transparent transparent ${COLORS.white} transparent;
  }

  &.bottom::before {
    top: -13px;
    left: calc(50% - 12px);
  }

  &.top::before {
    bottom: -14px;
    left: calc(50% - 12px);
    transform: rotate(180deg);
  }

  &.left::before {
    bottom: calc(50% - 12px);
    right: -18px;
    transform: rotate(90deg);
  }

  &.bottomRight::before {
    top: -14px;
    left: 24px;
  }

  ${media.iwMobile} {
    &:not(.bottomRight)&::before {
      top: -13px;
      left: calc(50% - 12px);
      transform: rotate(0);
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressText = styled.div`
  color: ${COLORS.systemDark200};
  font-weight: 700;
`;

const Text = styled.div`
  font-size: 14px;
  margin: 16px 0;
`;

const FixedContainer = styled.div`
  position: fixed;
  left: 22px;
  border-radius: 12px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.systemSky200};
  pointer-events: none;
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.03),
    0 1px 6px -1px rgba(0, 0, 0, 0.02),
    0 2px 4px 0 rgba(0, 0, 0, 0.02);
  font-size: 14px;

  transition: opacity ${FADE_DURATION}ms 50ms ease-in-out;

  ${media.iwMobile} {
    pointer-events: all;
    overflow: auto;
  }
`;
